'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	XMarkIcon,
	CreditCardIcon,
	BanknotesIcon,
	CalendarIcon,
	UserIcon,
	ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import Button from './button';
import { toast } from 'sonner';

const DirectDebitModal = ({ closeModal }) => {
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(1);
	const [directDebitData, setDirectDebitData] = useState(null);
	const [existingDebits, setExistingDebits] = useState([]);
	const [loadingDebits, setLoadingDebits] = useState(true);

	const { user } = useAuth();
	const {
		register,
		handleSubmit,
		formState: { errors },
		watch,
		setValue,
		reset,
	} = useForm();

	useEffect(() => {
		fetchExistingDebits();
	}, []);

	const fetchExistingDebits = async () => {
		try {
			setLoadingDebits(true);
			const response = await apiService.getDirectDebits();
			if (response.data.status === 200) {
				setExistingDebits(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching direct debits:', error);
		} finally {
			setLoadingDebits(false);
		}
	};

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			if (step === 1) {
				// Step 1: Create direct debit
				const response = await apiService.createDirectDebit(data);
				if (response.data.status === 201) {
					setDirectDebitData(response.data.data);
					setStep(2);
					toast.success('Direct debit created successfully!');
				}
			} else if (step === 2) {
				// Step 2: Process direct debit
				const response = await apiService.processDirectDebit(
					directDebitData.id,
				);
				if (response.data.status === 200) {
					toast.success('Direct debit processed successfully!');
					closeModal();
				}
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to process direct debit';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = async (debitId) => {
		try {
			const response = await apiService.cancelDirectDebit(debitId);
			if (response.data.status === 200) {
				toast.success('Direct debit cancelled successfully!');
				fetchExistingDebits();
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to cancel direct debit';
			toast.error(message);
		}
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-NG', {
			style: 'currency',
			currency: 'NGN',
		}).format(amount);
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-gray-200">
					<div className="flex items-center">
						<div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
							<BanknotesIcon className="h-6 w-6 text-green-600" />
						</div>
						<div>
							<h3 className="text-lg font-medium text-gray-900">
								Direct Debit Management
							</h3>
							<p className="text-sm text-gray-500">
								{step === 1
									? 'Set up automated debt collections'
									: 'Review and confirm direct debit'}
							</p>
						</div>
					</div>
					<button
						onClick={closeModal}
						className="text-gray-400 hover:text-gray-600 transition-colors">
						<XMarkIcon className="h-6 w-6" />
					</button>
				</div>

				{/* Content */}
				<div className="p-6">
					{step === 1 && (
						<>
							{/* Existing Direct Debits */}
							{!loadingDebits && existingDebits.length > 0 && (
								<div className="mb-6">
									<h4 className="text-md font-medium text-gray-900 mb-3">
										Active Direct Debits
									</h4>
									<div className="space-y-3">
										{existingDebits.map((debit) => (
											<div
												key={debit.id}
												className="border border-gray-200 rounded-lg p-4">
												<div className="flex items-center justify-between">
													<div className="flex-1">
														<div className="flex items-center">
															<UserIcon className="h-4 w-4 text-gray-400 mr-2" />
															<span className="text-sm font-medium text-gray-900">
																{debit.debtorName}
															</span>
														</div>
														<div className="mt-1 text-sm text-gray-500">
															{formatCurrency(debit.amount)} -{' '}
															{debit.frequency} - Next:{' '}
															{new Date(
																debit.nextPaymentDate,
															).toLocaleDateString()}
														</div>
														<span
															className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${
																debit.status === 'ACTIVE'
																	? 'bg-green-100 text-green-800'
																	: debit.status === 'PAUSED'
																	? 'bg-yellow-100 text-yellow-800'
																	: 'bg-red-100 text-red-800'
															}`}>
															{debit.status}
														</span>
													</div>
													<div className="flex space-x-2">
														<Button
															type="danger"
															variant="outlined"
															label="Cancel"
															size="S"
															onClick={() => handleCancel(debit.id)}
														/>
													</div>
												</div>
											</div>
										))}
									</div>
									<hr className="my-6" />
								</div>
							)}

							{/* Create New Direct Debit Form */}
							<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
								<div>
									<h4 className="text-md font-medium text-gray-900 mb-4">
										Create New Direct Debit
									</h4>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{/* Debtor Information */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Debtor Name *
											</label>
											<input
												{...register('debtorName', {
													required: 'Debtor name is required',
												})}
												type="text"
												className="input-field"
												placeholder="Enter debtor's full name"
											/>
											{errors.debtorName && (
												<p className="mt-1 text-sm text-red-600">
													{errors.debtorName.message}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Debtor Email *
											</label>
											<input
												{...register('debtorEmail', {
													required: 'Email is required',
													pattern: {
														value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
														message: 'Invalid email address',
													},
												})}
												type="email"
												className="input-field"
												placeholder="debtor@example.com"
											/>
											{errors.debtorEmail && (
												<p className="mt-1 text-sm text-red-600">
													{errors.debtorEmail.message}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Debtor Phone
											</label>
											<input
												{...register('debtorPhone')}
												type="tel"
												className="input-field"
												placeholder="+234-XXX-XXX-XXXX"
											/>
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Bank Verification Number (BVN) *
											</label>
											<input
												{...register('bvn', {
													required: 'BVN is required',
													pattern: {
														value: /^\d{11}$/,
														message: 'BVN must be 11 digits',
													},
												})}
												type="text"
												className="input-field"
												placeholder="Enter 11-digit BVN"
												maxLength="11"
											/>
											{errors.bvn && (
												<p className="mt-1 text-sm text-red-600">
													{errors.bvn.message}
												</p>
											)}
										</div>
									</div>
								</div>

								{/* Payment Details */}
								<div>
									<h5 className="text-sm font-medium text-gray-900 mb-3">
										Payment Details
									</h5>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Amount (NGN) *
											</label>
											<input
												{...register('amount', {
													required: 'Amount is required',
													min: {
														value: 100,
														message: 'Minimum amount is ₦100',
													},
													max: {
														value: 1000000,
														message: 'Maximum amount is ₦1,000,000',
													},
												})}
												type="number"
												step="0.01"
												className="input-field"
												placeholder="0.00"
											/>
											{errors.amount && (
												<p className="mt-1 text-sm text-red-600">
													{errors.amount.message}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Frequency *
											</label>
											<select
												{...register('frequency', {
													required: 'Frequency is required',
												})}
												className="input-field">
												<option value="">Select frequency</option>
												<option value="WEEKLY">Weekly</option>
												<option value="MONTHLY">Monthly</option>
												<option value="QUARTERLY">Quarterly</option>
												<option value="ANNUALLY">Annually</option>
											</select>
											{errors.frequency && (
												<p className="mt-1 text-sm text-red-600">
													{errors.frequency.message}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Start Date *
											</label>
											<input
												{...register('startDate', {
													required: 'Start date is required',
												})}
												type="date"
												className="input-field"
												min={new Date().toISOString().split('T')[0]}
											/>
											{errors.startDate && (
												<p className="mt-1 text-sm text-red-600">
													{errors.startDate.message}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												End Date (Optional)
											</label>
											<input
												{...register('endDate')}
												type="date"
												className="input-field"
												min={watch('startDate')}
											/>
										</div>
									</div>
								</div>

								{/* Additional Information */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Description/Notes
									</label>
									<textarea
										{...register('description')}
										rows="3"
										className="input-field"
										placeholder="Optional description or notes about this direct debit..."
									/>
								</div>

								{/* Actions */}
								<div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
									<Button
										type="secondary"
										variant="outlined"
										label="Cancel"
										onClick={closeModal}
									/>
									<Button
										type="primary"
										label="Create Direct Debit"
										loading={loading}
										disabled={loading}
										iconLeft={<CreditCardIcon className="h-4 w-4" />}
									/>
								</div>
							</form>
						</>
					)}

					{step === 2 && directDebitData && (
						<>
							{/* Confirmation Step */}
							<div className="text-center mb-6">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<CreditCardIcon className="h-8 w-8 text-green-600" />
								</div>
								<h4 className="text-lg font-medium text-gray-900 mb-2">
									Direct Debit Created Successfully
								</h4>
								<p className="text-sm text-gray-500">
									Review the details below and confirm to activate
								</p>
							</div>

							{/* Direct Debit Summary */}
							<div className="bg-gray-50 rounded-lg p-4 mb-6">
								<h5 className="text-sm font-medium text-gray-900 mb-3">
									Direct Debit Summary
								</h5>
								<div className="grid grid-cols-2 gap-4 text-sm">
									<div>
										<span className="text-gray-500">Debtor:</span>
										<span className="ml-2 font-medium">
											{directDebitData.debtorName}
										</span>
									</div>
									<div>
										<span className="text-gray-500">Amount:</span>
										<span className="ml-2 font-medium">
											{formatCurrency(directDebitData.amount)}
										</span>
									</div>
									<div>
										<span className="text-gray-500">Frequency:</span>
										<span className="ml-2 font-medium">
											{directDebitData.frequency}
										</span>
									</div>
									<div>
										<span className="text-gray-500">Start Date:</span>
										<span className="ml-2 font-medium">
											{new Date(
												directDebitData.startDate,
											).toLocaleDateString()}
										</span>
									</div>
									<div>
										<span className="text-gray-500">Status:</span>
										<span className="ml-2">
											<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
												{directDebitData.status}
											</span>
										</span>
									</div>
									<div>
										<span className="text-gray-500">Reference:</span>
										<span className="ml-2 font-mono text-xs">
											{directDebitData.reference}
										</span>
									</div>
								</div>
							</div>

							{/* Warning */}
							<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
								<div className="flex">
									<ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
									<div>
										<h5 className="text-sm font-medium text-yellow-800">
											Important Notice
										</h5>
										<p className="text-sm text-yellow-700 mt-1">
											By activating this direct debit, you authorize automatic
											collection of the specified amount according to the
											schedule. The debtor will receive notification of this
											setup.
										</p>
									</div>
								</div>
							</div>

							{/* Actions */}
							<div className="flex justify-end space-x-3">
								<Button
									type="secondary"
									variant="outlined"
									label="Back"
									onClick={() => setStep(1)}
								/>
								<Button
									type="primary"
									label="Activate Direct Debit"
									loading={loading}
									disabled={loading}
									onClick={handleSubmit(onSubmit)}
									iconLeft={<CalendarIcon className="h-4 w-4" />}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default DirectDebitModal;
